import React, { useState, useRef, useEffect, useContext, useCallback } from 'react'
import { useLogout } from '../../auth/hooks/useLogout';
import { BreadCrumbContext } from '../../master/context/BreadCrumbContext';
import { socket } from '../../../config/SocketApi';
import { useNavigate } from 'react-router-dom';
import Peer from 'simple-peer';
import { Backdrop, Box, Button, Typography, styled, useTheme, CircularProgress, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Quiz as QuizIcon } from '@mui/icons-material';
import { getIceConfig } from '../../../config/iceConfig';
import { SOCKET_EVENTS } from '../../../config/socketEvents';
import { bindSocketEvents, destroyPeer, emitReadinessWithRetry, safeSignalPeer, stopMediaStream } from '../helpers/realtimeUtils';

// MOBILE BREAKPOINT - must match index.js
const MOBILE_BREAKPOINT = 1000;

const StudentBoard = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { logout } = useLogout();
    const navigate = useNavigate();
    const { breadCrumbSetter } = useContext(BreadCrumbContext)
    const [tutorStream, setTutorStream] = useState();
    const [notAuthenticated, setNotAuthenticated] = useState(false);
    const [pollDialogOpen, setPollDialogOpen] = useState(false);
    const studentVideoRef = useRef()
    const [whiteBoardDimensions, setWhiteBoardDimensions] = useState()
    const studentPeerRef = useRef();
    const tutorStreamRef = useRef();
    const animationFrameRef = useRef();
    const containerRef = useRef();
    const whiteboardReadyTimerRef = useRef();
    const cancelWhiteboardReadinessRef = useRef();
    const cancelScreenShareReadinessRef = useRef();
    const screenShareActiveRef = useRef(false);
    const activeSourceRef = useRef('whiteboard');
    const lastLandscapeRef = useRef(window.innerWidth >= window.innerHeight);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
    const [mediaSize, setMediaSize] = useState({ width: 0, height: 0 });
    
    // Use media query for mobile detection - must match index.js breakpoint
    const isMobile = useMediaQuery(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    
    // Update container size on mount, resize, and mobile orientation changes.
    useEffect(() => {
        const updateContainerSize = () => {
            if (containerRef.current) {
                const { clientWidth, clientHeight } = containerRef.current;
                setContainerSize({ width: clientWidth, height: clientHeight });
                console.log('[StudentBoard] Container size updated:', { width: clientWidth, height: clientHeight });
            }
        };

        const updateAfterOrientationSettles = () => {
            setTimeout(updateContainerSize, 80);
            setTimeout(updateContainerSize, 250);
        };

        // Initial update after a brief delay to let layout settle
        const timer = setTimeout(updateContainerSize, 100);

        // Update on resize
        window.addEventListener('resize', updateContainerSize);
        window.addEventListener('orientationchange', updateAfterOrientationSettles);
        window.visualViewport?.addEventListener('resize', updateContainerSize);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', updateContainerSize);
            window.removeEventListener('orientationchange', updateAfterOrientationSettles);
            window.visualViewport?.removeEventListener('resize', updateContainerSize);
        };
    }, []);
    
    const handleLogOut = () => {
        logout();
        breadCrumbSetter(0)
        navigate("/")
    };

    const requestWhiteboardStream = useCallback((reason = 'ready') => {
        if (screenShareActiveRef.current) return;
        cancelWhiteboardReadinessRef.current?.();
        cancelWhiteboardReadinessRef.current = emitReadinessWithRetry(
            socket,
            'student-ready-for-whiteboard',
            {
                reason,
                room_id: String(socket.auth?.room_id || ''),
                readiness_version: 2,
            }
        );
    }, []);

    const requestScreenShareStream = useCallback((reason = 'ready') => {
        cancelScreenShareReadinessRef.current?.();
        cancelScreenShareReadinessRef.current = emitReadinessWithRetry(
            socket,
            'student-ready-for-screen-share',
            {
                reason,
                room_id: String(socket.auth?.room_id || ''),
                readiness_version: 1,
            }
        );
    }, []);

    useEffect(() => {
        requestWhiteboardStream('mount');

        const handleConnect = () => {
            requestWhiteboardStream('connect');
            if (screenShareActiveRef.current) requestScreenShareStream('connect');
        };
        const scheduleOrientationRequest = () => {
            clearTimeout(whiteboardReadyTimerRef.current);
            whiteboardReadyTimerRef.current = setTimeout(
                () => {
                    requestWhiteboardStream('orientation-change');
                    if (screenShareActiveRef.current) requestScreenShareStream('orientation-change');
                },
                250
            );
        };
        const handleOrientationChange = () => {
            lastLandscapeRef.current = window.innerWidth >= window.innerHeight;
            scheduleOrientationRequest();
        };
        const handleViewportResize = () => {
            const viewport = window.visualViewport;
            const isLandscape = (viewport?.width || window.innerWidth) >= (viewport?.height || window.innerHeight);
            if (isLandscape === lastLandscapeRef.current) return;
            lastLandscapeRef.current = isLandscape;
            scheduleOrientationRequest();
        };

        socket.on('connect', handleConnect);
        window.addEventListener('orientationchange', handleOrientationChange);
        window.visualViewport?.addEventListener('resize', handleViewportResize);

        return () => {
            clearTimeout(whiteboardReadyTimerRef.current);
            cancelWhiteboardReadinessRef.current?.();
            cancelScreenShareReadinessRef.current?.();
            socket.off('connect', handleConnect);
            window.removeEventListener('orientationchange', handleOrientationChange);
            window.visualViewport?.removeEventListener('resize', handleViewportResize);
        };
    }, [requestScreenShareStream, requestWhiteboardStream]);

    useEffect(() => {
        tutorStreamRef.current = tutorStream;
    }, [tutorStream]);
    
    const createStudentPeerWhiteBoard = (data) => {
        if (data?.room_id && String(data.room_id) !== String(socket.auth?.room_id || '')) return;
        if (screenShareActiveRef.current) {
            console.log('[StudentBoard] Ignoring whiteboard signal while screen sharing is active');
            return;
        }
        console.log('[StudentBoard] createStudentPeerWhiteBoard called with data:', JSON.stringify(data?.whiteBoardDimensions));

        if (data?.signal?.type === 'offer') {
            destroyPeer(studentPeerRef.current, "StudentBoard replace whiteboard peer");
            const newPeer = new Peer({
                initiator: false,
                trickle: true,
                config: getIceConfig()
            });

            safeSignalPeer(newPeer, data.signal, "StudentBoard whiteboard offer")
            newPeer.on("stream", (stream) => {
                console.log('[StudentBoard] Received stream from teacher');
                activeSourceRef.current = 'whiteboard';
                stopMediaStream(tutorStreamRef.current);
                if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)
                const settings = stream.getVideoTracks?.()[0]?.getSettings?.() || {};
                setMediaSize({ width: Number(settings.width) || 0, height: Number(settings.height) || 0 });
                setTutorStream(stream)
            })
            newPeer.on("signal", (signal) => {
                socket.emit("white-board-student-signal", { signal, tutor_id: data.tutor_id });
            });
            newPeer.on('error', (err) => { console.log("[StudentBoard] Peer error:", err.message) })
            studentPeerRef.current = newPeer;
        } else {
            safeSignalPeer(studentPeerRef.current, data.signal, "StudentBoard whiteboard answer");
        }
        console.log('[StudentBoard] Setting whiteBoardDimensions:', JSON.stringify(data?.whiteBoardDimensions));
        setWhiteBoardDimensions(data?.whiteBoardDimensions)
    }
    
    const createStudentPeerShareScreen = (data) => {
        if (data?.room_id && String(data.room_id) !== String(socket.auth?.room_id || '')) return;
        if (data?.signal?.type === 'offer') {
            console.log('[StudentBoard] Received screen-share offer from:', data?.tutor_id);
            screenShareActiveRef.current = true;
            activeSourceRef.current = 'screen-share';
            cancelWhiteboardReadinessRef.current?.();
            cancelScreenShareReadinessRef.current?.();
            destroyPeer(studentPeerRef.current, "StudentBoard replace share-screen peer");
            const newPeer = new Peer({
                initiator: false,
                trickle: true,
                config: getIceConfig()
            });

            safeSignalPeer(newPeer, data.signal, "StudentBoard share-screen offer")
            newPeer.on("stream", (stream) => {
                console.log('[StudentBoard] Received screen-share stream:', stream?.id);
                activeSourceRef.current = 'screen-share';
                stopMediaStream(tutorStreamRef.current);
                if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)
                const settings = stream.getVideoTracks?.()[0]?.getSettings?.() || {};
                setMediaSize({ width: Number(settings.width) || 0, height: Number(settings.height) || 0 });
                setTutorStream(stream)
            })
            newPeer.on("signal", (signal) => {
                console.log('[StudentBoard] Sending screen-share response:', signal?.type || 'candidate');
                socket.emit("screen-sharing-student-signal", { signal, tutor_id: data.tutor_id });
            });
            newPeer.on('connect', () => console.log('[StudentBoard] Screen-share peer connected'));
            newPeer.on('close', () => console.log('[StudentBoard] Screen-share peer closed'));
            newPeer.on('error', (err) => { console.log("[StudentBoard] Screen-share peer error:", err?.message || err) })
            studentPeerRef.current = newPeer;
        } else {
            safeSignalPeer(studentPeerRef.current, data.signal, "StudentBoard share-screen answer");
        }
        setWhiteBoardDimensions(data?.whiteBoardDimensions)
    }
    
    // Reference to store the last frame before pausing
    const lastFrameRef = useRef(null);
    const canvasRef = useRef(null);
    
    // Force re-render on window resize
    const [, forceUpdate] = useState(0);
    
    useEffect(() => {
        const handleResize = () => {
            forceUpdate(n => n + 1);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    useEffect(() => {
        const handleWhiteBoardSignal = (data) => {
            createStudentPeerWhiteBoard(data);
        };
        
        const handleScreenShareSignal = (data) => {
            createStudentPeerShareScreen(data);
        };
        const handleScreenShareDimensions = () => {
            screenShareActiveRef.current = true;
            activeSourceRef.current = 'screen-share';
            cancelWhiteboardReadinessRef.current?.();
            requestScreenShareStream('screen-share-started');
        };
        const handleClosedScreenShare = () => {
            screenShareActiveRef.current = false;
            activeSourceRef.current = 'whiteboard';
            cancelScreenShareReadinessRef.current?.();
            destroyPeer(studentPeerRef.current, 'StudentBoard screen-share ended');
            studentPeerRef.current = null;
            stopMediaStream(tutorStreamRef.current);
            tutorStreamRef.current = null;
            setTutorStream(null);
            requestWhiteboardStream('screen-share-ended');
        };
        
        const handlePollDialogState = (data) => {
            setPollDialogOpen(data.isOpen);
            
            if (data.isOpen && studentVideoRef.current) {
                if (canvasRef.current && studentVideoRef.current.videoWidth > 0) {
                    const ctx = canvasRef.current.getContext('2d');
                    canvasRef.current.width = studentVideoRef.current.videoWidth;
                    canvasRef.current.height = studentVideoRef.current.videoHeight;
                    ctx.drawImage(studentVideoRef.current, 0, 0);
                    lastFrameRef.current = canvasRef.current.toDataURL();
                }
                studentVideoRef.current.pause();
            }
            if (!data.isOpen && studentVideoRef.current) {
                studentVideoRef.current.play().catch(e => console.log("[StudentBoard] Resume play error:", e));
            }
        };
        
        const handleNotAuthenticated = () => {
            socket.disconnect();
            setNotAuthenticated(true)
            setTimeout(() => {
                handleLogOut();
            }, 5000);
        };
        const handleConnectError = (error) => console.log("[StudentBoard] Socket connect error:", error);

        const unbindEvents = bindSocketEvents(socket, [
            { event: SOCKET_EVENTS.LEGACY_WHITEBOARD_TUTOR_SIGNAL, handler: handleWhiteBoardSignal },
            { event: SOCKET_EVENTS.LEGACY_SCREEN_SHARING_TUTOR_SIGNAL, handler: handleScreenShareSignal },
            { event: "screen-sharing-dimentions", handler: handleScreenShareDimensions },
            { event: "closed-sharing-stream", handler: handleClosedScreenShare },
            { event: "poll-dialog-state", handler: handlePollDialogState },
            { event: "not-atuthenticated", handler: handleNotAuthenticated },
            { event: "connect_error", handler: handleConnectError }
        ]);
        
        return () => {
            unbindEvents();
            stopMediaStream(tutorStreamRef.current);
            if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)
            setTutorStream(null)
            destroyPeer(studentPeerRef.current, "StudentBoard cleanup");
            studentPeerRef.current = null
        }
    }, [requestScreenShareStream, requestWhiteboardStream])

    useEffect(() => {
        const videoElement = studentVideoRef.current;
        if (videoElement && tutorStream) {
            if (videoElement.srcObject !== tutorStream) {
                videoElement.srcObject = tutorStream;
            }
            videoElement.play().catch(err => {
                if (err.name !== 'AbortError') {
                    console.error("[StudentBoard] Video play error:", err.message);
                }
            });
        }
    }, [tutorStream, isMobile, containerSize.width, containerSize.height])

    useEffect(() => () => {
        const videoElement = studentVideoRef.current;
        if (videoElement) {
            videoElement.srcObject = null;
        }
    }, []);

    const NotAuthenticatedWrapper = styled(Box)({
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${theme.palette.text.secondary}`,
        margin: 'auto',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: theme.palette.background.default,
        padding: 20
    })
    
    const ButtonsRow = styled(Box)({
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        margin: 'auto',
        justifyContent: 'flex-start',
        alignItems: 'center',
        marginTop: 20,
        backgroundColor: theme.palette.background.default,
        gap: 20
    })
    
    // Calculate display dimensions for the whiteboard
    const getDisplayDimensions = () => {
        if (!whiteBoardDimensions) return null;
        
        const { x, y, width, height, fullWidth, fullHeight, capture } = whiteBoardDimensions;
        
        console.log('[StudentBoard] Calculating display with whiteboard dimensions:', { x, y, width, height, fullWidth, fullHeight });
        console.log('[StudentBoard] Container size state:', containerSize);
        
        if (!width || !height || width <= 0 || height <= 0) {
            console.log('[StudentBoard] Invalid dimensions, returning null');
            return null;
        }
        
        const sourceWidth = mediaSize.width || capture?.sourceWidth || fullWidth;
        const sourceHeight = mediaSize.height || capture?.sourceHeight || fullHeight;
        const frameWidth = capture?.frameWidth || fullWidth;
        const frameHeight = capture?.frameHeight || fullHeight;
        const sourceScaleX = sourceWidth / Math.max(frameWidth, 1);
        const sourceScaleY = sourceHeight / Math.max(frameHeight, 1);
        const cropX = ((capture?.contentOffsetX || 0) + x) * sourceScaleX;
        const cropY = ((capture?.contentOffsetY || 0) + y) * sourceScaleY;
        const cropWidth = width * sourceScaleX;
        const cropHeight = height * sourceScaleY;
        const regionAspectRatio = cropWidth / cropHeight;
        
        // MOBILE (Landscape): Full screen, crop to whiteboard region
        if (isMobile) {
            const viewportWidth = containerSize.width || window.visualViewport?.width || window.innerWidth;
            const viewportHeight = containerSize.height || window.visualViewport?.height || window.innerHeight;
            const viewportAspectRatio = viewportWidth / viewportHeight;
            
            // Scale to COVER the viewport
            const scale = viewportAspectRatio > regionAspectRatio
                ? viewportWidth / cropWidth
                : viewportHeight / cropHeight;

            const scaledVideoWidth = sourceWidth * scale;
            const scaledVideoHeight = sourceHeight * scale;

            const baseOffsetX = -cropX * scale;
            const baseOffsetY = -cropY * scale;

            const scaledRegionWidth = cropWidth * scale;
            const scaledRegionHeight = cropHeight * scale;
            const centerOffsetX = (viewportWidth - scaledRegionWidth) / 2;
            const centerOffsetY = (viewportHeight - scaledRegionHeight) / 2;
            
            const offsetX = baseOffsetX + centerOffsetX;
            const offsetY = baseOffsetY + centerOffsetY;
            
            console.log('[StudentBoard] Mobile dimensions calculated:', { 
                scale, scaledVideoWidth, scaledVideoHeight, offsetX, offsetY 
            });
            
            return {
                containerWidth: viewportWidth,
                containerHeight: viewportHeight,
                scaledVideoWidth,
                scaledVideoHeight,
                offsetX,
                offsetY,
                isMobileView: true
            };
        }
        
        // DESKTOP: Fill available space while maintaining aspect ratio
        // Use state for container size (more reliable than direct ref access)
        let availableWidth = containerSize.width || 800;
        let availableHeight = containerSize.height || 600;
        
        // Fallback if state is not set yet
        if (availableWidth <= 0 || availableHeight <= 0) {
            const containerEl = containerRef.current;
            if (containerEl) {
                availableWidth = containerEl.clientWidth;
                availableHeight = containerEl.clientHeight;
            } else {
                // Last resort fallback
                availableWidth = window.innerWidth - 300 - 24;
                availableHeight = window.innerHeight - 60 - 16;
            }
        }
        
        console.log('[StudentBoard] Available space:', { availableWidth, availableHeight });
        
        const containerWidth = availableWidth;
        const containerHeight = availableHeight;
        const scale = Math.min(containerWidth / cropWidth, containerHeight / cropHeight);
        const renderedRegionWidth = cropWidth * scale;
        const renderedRegionHeight = cropHeight * scale;

        // Scale full video dimensions
        const scaledVideoWidth = sourceWidth * scale;
        const scaledVideoHeight = sourceHeight * scale;

        const cropLeft = (containerWidth - renderedRegionWidth) / 2;
        const cropTop = (containerHeight - renderedRegionHeight) / 2;
        const offsetX = -cropX * scale;
        const offsetY = -cropY * scale;
        
        console.log('[StudentBoard] Desktop dimensions calculated:', {
            containerWidth, containerHeight,
            scaledVideoWidth, scaledVideoHeight,
            offsetX, offsetY,
            scale,
            regionAspectRatio
        });
        
        return {
            containerWidth,
            containerHeight,
            scaledVideoWidth,
            scaledVideoHeight,
            cropLeft,
            cropTop,
            cropWidth: renderedRegionWidth,
            cropHeight: renderedRegionHeight,
            offsetX,
            offsetY,
            isMobileView: false
        };
    };
    
    // Recalculate displayDims when whiteBoardDimensions or containerSize changes
    const displayDims = React.useMemo(() => {
        if (!whiteBoardDimensions) return null;
        return getDisplayDimensions();
    }, [whiteBoardDimensions, containerSize, isMobile, mediaSize]);
    
    // MOBILE: Full screen layout
    if (isMobile) {
        return (
            <Box 
                ref={containerRef}
                sx={{ 
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden',
                    backgroundColor: '#000',
                }}
            >
                <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={notAuthenticated}>
                    <NotAuthenticatedWrapper>
                        <Typography variant='h5' align='center'>{t("not_authenticated")}</Typography>
                        <ButtonsRow>
                            <Button variant='contained' color='secondary' onClick={handleLogOut}>
                                {t("understand")}
                            </Button>
                        </ButtonsRow>
                    </NotAuthenticatedWrapper>
                </Backdrop>
                
                {/* Poll Dialog Overlay */}
                {pollDialogOpen && (
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(0, 0, 0, 0.85)',
                        zIndex: 100,
                        gap: 2,
                    }}>
                        <QuizIcon sx={{ fontSize: 48, color: '#667eea' }} />
                        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                            {t('quizComingSoon') || 'Quiz Coming Soon!'}
                        </Typography>
                        <CircularProgress size={28} sx={{ color: '#667eea', mt: 1 }} />
                    </Box>
                )}
                
                {/* Loading state */}
                {!tutorStream && (
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#0a0a14',
                    }}>
                        <CircularProgress sx={{ color: '#667eea' }} />
                    </Box>
                )}
                
                {/* Video container - FULL SCREEN */}
                {displayDims && (
                    <Box sx={{
                        width: '100%',
                        height: '100%',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        overflow: 'hidden',
                    }} data-testid="whiteboard-viewport">
                        <video
                            data-testid="whiteboard-video"
                            data-stream-source={activeSourceRef.current}
                            ref={studentVideoRef}
                            autoPlay
                            playsInline
                            muted
                            onLoadedMetadata={(event) => setMediaSize({
                                width: event.currentTarget.videoWidth,
                                height: event.currentTarget.videoHeight,
                            })}
                            style={{
                                position: 'absolute',
                                width: displayDims.scaledVideoWidth,
                                height: displayDims.scaledVideoHeight,
                                left: displayDims.offsetX,
                                top: displayDims.offsetY,
                                objectFit: 'fill',
                            }}
                        />
                    </Box>
                )}
                
                <canvas ref={canvasRef} style={{ display: 'none' }} />
            </Box>
        );
    }
    
    // DESKTOP: Fill available container space
    return (
        <Box 
            ref={containerRef}
            sx={{ 
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                backgroundColor: '#0a0a14',
                position: 'relative',
            }}
        >
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={notAuthenticated}>
                <NotAuthenticatedWrapper>
                    <Typography variant='h5' align='center'>{t("not_authenticated")}</Typography>
                    <ButtonsRow>
                        <Button variant='contained' color='secondary' onClick={handleLogOut}>
                            {t("understand")}
                        </Button>
                    </ButtonsRow>
                </NotAuthenticatedWrapper>
            </Backdrop>
            
            {/* Poll Dialog Overlay */}
            {pollDialogOpen && (
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.85)',
                    zIndex: 100,
                    gap: 2,
                }}>
                    <QuizIcon sx={{ fontSize: 64, color: '#667eea' }} />
                    <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600 }}>
                        {t('quizComingSoon') || 'Quiz Coming Soon!'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        {t('teacherPreparingQuiz') || 'The teacher is preparing a quiz for you...'}
                    </Typography>
                    <CircularProgress size={32} sx={{ color: '#667eea', mt: 2 }} />
                </Box>
            )}
            
            {/* Loading state */}
            {!tutorStream && (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                }}>
                    <CircularProgress sx={{ color: '#667eea' }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                        {t('waitingForTeacher') || 'Waiting for teacher...'}
                    </Typography>
                </Box>
            )}
            
            {/* Video container - FILL available space */}
            {displayDims && (
                <Box sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden',
                    borderRadius: '8px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                }} data-testid="whiteboard-viewport">
                    <Box
                        data-testid="whiteboard-crop"
                        sx={{
                            position: 'absolute',
                            left: displayDims.cropLeft,
                            top: displayDims.cropTop,
                            width: displayDims.cropWidth,
                            height: displayDims.cropHeight,
                            overflow: 'hidden',
                            bgcolor: '#0a0a14',
                        }}
                    >
                        <video
                            data-testid="whiteboard-video"
                            ref={studentVideoRef}
                            autoPlay
                            playsInline
                            muted
                            onLoadedMetadata={(event) => setMediaSize({
                                width: event.currentTarget.videoWidth,
                                height: event.currentTarget.videoHeight,
                            })}
                            style={{
                                position: 'absolute',
                                width: displayDims.scaledVideoWidth,
                                height: displayDims.scaledVideoHeight,
                                left: displayDims.offsetX,
                                top: displayDims.offsetY,
                                objectFit: 'fill',
                            }}
                        />
                    </Box>
                </Box>
            )}
            
            <canvas ref={canvasRef} style={{ display: 'none' }} />
        </Box>
    );
}

export default StudentBoard
